/**
 * routeLookup — reverse index from a street address to the walking-route stop
 * that covers it. Powers the "click a voter's address → jump to their walking
 * route" drill-down in VoterDetailDrawer.
 *
 * SUBREGION_ROUTE_COORDS is keyed [subregion][shiftId] = ordered stops, each
 * with an `address`. Only a subset of voters live on a generated route, so a
 * lookup miss (returns null) is normal — the caller falls back to plain text.
 *
 * The index is built once, lazily, and memoized at module scope (the source
 * is a static ~46KB client bundle, so this is cheap and never refetched).
 */
import { SUBREGION_ROUTE_COORDS } from '@/data/subregionRouteCoords';

export interface RouteStopLocation {
  subregion: string;
  shiftId: string;
  stopIndex: number;
  address: string;
}

/** Normalize for matching: upper-case, collapse whitespace, trim. */
const norm = (s: string | null | undefined): string =>
  String(s || '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();

let index: Map<string, RouteStopLocation> | null = null;

function buildIndex(): Map<string, RouteStopLocation> {
  const map = new Map<string, RouteStopLocation>();
  for (const [subregion, shifts] of Object.entries(SUBREGION_ROUTE_COORDS)) {
    for (const [shiftId, stops] of Object.entries(
      shifts as Record<string, Array<{ address: string }>>,
    )) {
      stops.forEach((stop, stopIndex) => {
        const key = norm(stop.address);
        // First stop wins for a given address (routes shouldn't overlap, but
        // if they do we want a stable, deterministic target).
        if (key && !map.has(key)) {
          map.set(key, { subregion, shiftId, stopIndex, address: stop.address });
        }
      });
    }
  }
  return map;
}

/**
 * Which walking-route stop (if any) covers this address? Returns null when the
 * address isn't on any generated route.
 */
export function findRouteStopByAddress(
  address: string | null | undefined,
): RouteStopLocation | null {
  if (!address) return null;
  if (!index) index = buildIndex();
  return index.get(norm(address)) || null;
}
